import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AppComponent } from '../app.component';
import { ProfileService } from '../services/profile.service';
import { VoxPopService } from '../services/voxpop.service';
import { Programme } from '../types/programme';
import { UserProfile } from '../types/user-profile';

@Component({
  selector: 'profile-detail',
  templateUrl: './profile-detail.component.html',
  styleUrls: ['./profile-detail.component.css'],
  providers: [VoxPopService],
  standalone: false
})
export class ProfileDetailComponent implements OnInit {
  @ViewChild('editButton') editButton: ElementRef<HTMLButtonElement>;
  @ViewChild('cancelButton') cancelButton: ElementRef<HTMLButtonElement>;
  CanAdminister = ProfileService.CanAdministerAll;
  CanAdministerProgramme = ProfileService.CanAdministerProgramme;
  profile: UserProfile;
  profileDraft: UserProfile;
  programmes: Programme[] = [];
  errorMessage = '';
  programmeError = '';
  programmesLoading = false;
  saveMessage = '';
  editable = false;
  saving = false;

  constructor(private voxPopService: VoxPopService, private route: ActivatedRoute) { }

  ngOnInit(): void {
    if (!ProfileService.IsLoggedIn()) { return; }
    this.loadProgrammes();
    this.route.params.subscribe(() => this.loadProfile());
  }
  loadProgrammes(): void {
    this.programmesLoading = true;
    this.programmeError = '';
    this.voxPopService.myActiveProgrammes(true).subscribe(
      programmes => { this.programmes = programmes || []; this.programmesLoading = false; },
      () => { this.programmeError = 'Could not load programme rights. Please retry.'; this.programmesLoading = false; },
    );
  }
  loadProfile(): void {
    this.errorMessage = '';
    this.voxPopService.getProfile(+this.route.snapshot.params['profiletype'], this.route.snapshot.params['profileid']).subscribe(
      profile => this.setProfile(profile),
      () => this.errorMessage = 'Could not load reviewer details. Please retry.',
    );
  }
  setProfile(profile: UserProfile): void {
    this.profile = profile;
    AppComponent.SetTitle(profile.social?.name || 'Reviewer');
  }
  edit(): void {
    this.profileDraft = Object.assign(new UserProfile(), this.profile, {
      social: { ...this.profile.social },
      // The API uses a JSON object, despite the legacy Map annotation.
      programme_rights: { ...this.profile.programme_rights },
    });
    this.errorMessage = this.saveMessage = '';
    this.editable = true;
    setTimeout(() => this.cancelButton?.nativeElement.focus());
  }
  cancel(): void {
    if (this.saving) { return; }
    this.editable = false;
    this.profileDraft = null;
    this.errorMessage = '';
    setTimeout(() => this.editButton?.nativeElement.focus());
  }
  ownProfile(): boolean { return ProfileService.CurrentUserProfile()?.id === this.profile.id; }
  globalRightsDisabled(): boolean { return this.saving || this.ownProfile() || !this.CanAdminister(); }
  programmeRightsDisabled(): boolean { return this.saving || (this.ownProfile() && !this.CanAdminister()); }
  hasRight(profile: UserProfile, programmeID: number, right: string): boolean {
    return !!profile.programme_rights?.[programmeID]?.includes(right);
  }
  save(): void {
    if (this.saving || !this.profileDraft) { return; }
    this.saving = true;
    this.errorMessage = '';
    this.voxPopService.updateProfile(this.profileDraft).subscribe(
      () => this.voxPopService.getProfile(this.profile.type, this.profile.id).subscribe(
        profile => {
          this.setProfile(profile);
          this.saving = this.editable = false;
          this.profileDraft = null;
          this.saveMessage = 'Reviewer saved.';
          setTimeout(() => this.editButton?.nativeElement.focus());
        },
        () => { this.saving = false; this.errorMessage = 'Reviewer saved, but details could not be refreshed. Reload to see the saved values. Your draft is still here.'; },
      ),
      () => { this.saving = false; this.errorMessage = 'Could not save reviewer. Your changes are still here. Please try Save again.'; },
    );
  }

  toggleAdminister(pID: number): void {
    if (this.saving || !this.profileDraft) { return; }
    let currentUserProfile = ProfileService.CurrentUserProfile();
    if (pID < 1) {
      if (currentUserProfile.id === this.profileDraft.id) {
        alert("You can't change your own rights!")
        return;
      } else {
        if (this.CanAdminister()) {
          this.profileDraft.administer = !this.profileDraft.administer;
        } else {
          alert("You do not have global administrator rights!");
          return;
        }
      }
    } else {
      if (currentUserProfile.id === this.profileDraft.id && !this.CanAdminister()) {
        alert("You can't change your own rights!")
        return;
      } else {
        let rights: string = this.profileDraft.programme_rights[pID];
        let newRights = "";
        if (rights && rights.length && rights.indexOf("r") >= 0) {
          newRights = "r";
        }
        if (!rights || !rights.length || rights.indexOf("A") < 0) {
          newRights += "A"
        }
        this.profileDraft.programme_rights[pID] = newRights;
      }
    }

  }

  toggleReview(pID: number): void {
    if (this.saving || !this.profileDraft) { return; }
    let currentUserProfile = ProfileService.CurrentUserProfile();
    if (pID < 1) {
      if (currentUserProfile.id === this.profileDraft.id) {
        alert("You can't change your own rights!")
        return;
      } else {
        if (this.CanAdminister()) {
          this.profileDraft.review = !this.profileDraft.review;
        } else {
          alert("You do not have global administrator rights!");
          return;
        }
      }
    } else {
      if (currentUserProfile.id === this.profileDraft.id && !this.CanAdminister()) {
        alert("You can't change your own rights!")
        return;
      } else {
        let rights: string = this.profileDraft.programme_rights[pID];
        let newRights = "";
        if (rights && rights.length && rights.indexOf("A") >= 0) {
          newRights = "A";
        }
        if (!rights || !rights.length || rights.indexOf("r") < 0) {
          newRights += "r"
        }
        this.profileDraft.programme_rights[pID] = newRights;
      }
    }

  }

}
